import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { Plus, Search, FileText, Calendar, Edit, Trash2, Upload, AlertTriangle } from 'lucide-react';
import { DataTable } from '../components/ui/data-table';
import { LoadingCard } from '../components/ui/loading-spinner';
import { invoicesApi, clientsApi, documentsApi, billingApi } from '../lib/api';
import { formatDate, formatCurrency, getStatusBadgeClass, getStatusLabel, getErrorMessage } from '../lib/utils';
import { toast } from 'sonner';
import InvoiceDialog from '../components/InvoiceDialog';
import UploadResultsDialog from '../components/UploadResultsDialog';

export default function Invoices() {
  const location = useLocation();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [clientFilter, setClientFilter] = useState('');
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const queryClient = useQueryClient();
  const fileInputRef = useRef(null);
  const [bulkResults, setBulkResults] = useState(null);

  // Тарифный флаг берём из /billing/subscription — единственный источник
  // правды (не дублируем список тарифов хардкодом на фронтенде).
  const { data: subscriptionData } = useQuery({
    queryKey: ['billing-subscription'],
    queryFn: () => billingApi.getSubscription().then((res) => res.data),
  });
  const hasDocRecognition = !!subscriptionData?.subscription?.has_document_recognition;

  // Проверяем URL параметры при загрузке
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('action') === 'new') {
      handleCreateNew();
    }
  }, [location.search]);

  const { data: invoicesData, isLoading } = useQuery({
    queryKey: ['invoices', { search, status: statusFilter, client_id: clientFilter }],
    queryFn: () => invoicesApi.getAll({ 
      search: search || undefined,
      status: statusFilter || undefined,
      client_id: clientFilter || undefined,
      skip: 0,
      limit: 50 
    }).then(res => res.data),
  });

  const { data: clientsData } = useQuery({
    queryKey: ['clients-for-filter'],
    queryFn: () => clientsApi.getAll({ limit: 100 }).then(res => res.data),
  });

  const deleteMutation = useMutation({
    mutationFn: invoicesApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      toast.success('Счёт успешно удалён');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, 'Ошибка при удалении счёта'));
    },
  });

  const handleEdit = (invoice) => {
    setSelectedInvoice(invoice);
    setIsDialogOpen(true);
  };

  const handleDelete = (invoice) => {
    if (window.confirm(`Вы уверены, что хотите удалить счёт "${invoice.number}"?`)) {
      deleteMutation.mutate(invoice.id);
    }
  };

  const handleCreateNew = () => {
    setSelectedInvoice(null);
    setIsDialogOpen(true);
  };

  const uploadInvoiceMutation = useMutation({
    mutationFn: (files) => documentsApi.uploadInvoicesBulk(files),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      const { results, summary } = res.data;
      setBulkResults({
        summary,
        results: results.map((r) => ({
          filename: r.filename,
          status: r.status,
          primary: r.status === 'error' ? null : `Счёт ${r.number}${r.client_created ? ' — создан новый клиент' : ''}`,
          detail: r.issues?.length ? r.issues.join('; ') : null,
        })),
      });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, 'Не удалось распознать файлы'));
    },
  });

  const handleUploadInvoice = (e) => {
    // Копируем в массив немедленно: e.target.files — живая ссылка на FileList,
    // и сброс value ниже (нужен, чтобы можно было выбрать те же файлы повторно)
    // очищает её до того, как асинхронная мутация успевает прочитать файлы.
    const files = Array.from(e.target.files || []);
    if (files.length) uploadInvoiceMutation.mutate(files);
    e.target.value = '';
  };

  const columns = [
    {
      header: 'Счёт',
      accessor: 'number',
      cell: (invoice) => (
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
            <FileText className="w-5 h-5 text-purple-600" />
          </div>
          <div>
            <p className="font-medium text-gray-900 flex items-center gap-1.5">
              {invoice.number}
              {invoice.needs_review && (
                <span
                  title={invoice.review_reason || 'Требует проверки — данные распознаны из PDF не полностью'}
                  data-testid={`needs-review-invoice-${invoice.id}`}
                >
                  <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" />
                </span>
              )}
            </p>
            {invoice.description && (
              <p className="text-sm text-gray-500 truncate max-w-xs">{invoice.description}</p>
            )}
          </div>
        </div>
      ),
    },
    {
      header: 'Клиент',
      accessor: 'client_id',
      cell: (invoice) => {
        const client = clientsData?.clients?.find(c => c.id === invoice.client_id);
        return (
          <div>
            <p className="text-sm text-gray-900">{client?.name || 'Неизвестен'}</p>
            {client?.company && (
              <p className="text-sm text-gray-500">{client.company}</p>
            )}
          </div>
        );
      },
    },
    {
      header: 'Статус',
      accessor: 'status',
      cell: (invoice) => (
        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadgeClass(invoice.status)}`}>
          {getStatusLabel('invoice', invoice.status)}
        </span>
      ),
    },
    {
      header: 'Сумма',
      accessor: 'amount',
      cell: (invoice) => (
        <div>
          <p className="text-sm text-gray-900 font-medium">{formatCurrency(invoice.amount || invoice.total || 0)}</p>
          {invoice.tax_amount > 0 && (
            <p className="text-sm text-gray-500">НДС: {formatCurrency(invoice.tax_amount)}</p>
          )}
        </div>
      ),
    },
    {
      header: 'Даты',
      accessor: 'dates',
      cell: (invoice) => (
        <div>
          <div className="flex items-center text-sm text-gray-600 mb-1">
            <Calendar className="w-4 h-4 mr-1" />
            <span>Выставлен: {formatDate(invoice.date_issued)}</span>
          </div>
          {invoice.date_due && (
            <div className="flex items-center text-sm text-gray-600">
              <Calendar className="w-4 h-4 mr-1" />
              <span>К оплате: {formatDate(invoice.date_due)}</span>
            </div>
          )}
        </div>
      ),
    },
    {
      header: 'Действия',
      accessor: 'actions',
      cell: (invoice) => (
        <div className="flex items-center space-x-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleEdit(invoice);
            }}
            className="text-blue-600 hover:text-blue-900 p-1 rounded"
            data-testid={`edit-invoice-${invoice.id}`}
          >
            <Edit className="w-4 h-4" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleDelete(invoice);
            }}
            className="text-red-600 hover:text-red-900 p-1 rounded"
            disabled={deleteMutation.isPending}
            data-testid={`delete-invoice-${invoice.id}`}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ),
    },
  ];

  const statusOptions = [
    { value: '', label: 'Все статусы' },
    { value: 'draft', label: 'Черновик' },
    { value: 'sent', label: 'Отправлен' },
    { value: 'partially_paid', label: 'Частично оплачен' },
    { value: 'paid', label: 'Оплачен' },
    { value: 'overdue', label: 'Просрочен' },
    { value: 'cancelled', label: 'Отменён' },
  ];

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900">Счета</h1>
        </div>
        <LoadingCard />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Заголовок и действия */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Счета</h1>
          <p className="text-gray-600 mt-1">
            Управление счетами ({invoicesData?.total || 0})
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <input
            type="file"
            accept="application/pdf"
            multiple
            ref={fileInputRef}
            onChange={handleUploadInvoice}
            className="hidden"
            data-testid="upload-invoice-input"
          />
          {hasDocRecognition ? (
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadInvoiceMutation.isPending}
              className="border border-purple-600 text-purple-600 px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-purple-50 transition-colors disabled:opacity-50"
              data-testid="upload-invoice-btn"
            >
              <Upload className="w-5 h-5" />
              <span>{uploadInvoiceMutation.isPending ? 'Распознаём...' : 'Загрузить PDF'}</span>
            </button>
          ) : (
            <a
              href="/billing"
              className="border border-gray-300 text-gray-400 px-4 py-2 rounded-lg flex items-center space-x-2"
              title="Загрузка счетов с автораспознаванием доступна на тарифе Professional и выше"
              data-testid="upload-invoice-upsell"
            >
              <Upload className="w-5 h-5" />
              <span>Загрузить PDF (Professional+)</span>
            </a>
          )}
          <button
            onClick={handleCreateNew}
            className="bg-purple-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-purple-700 transition-colors"
            data-testid="create-invoice-btn"
          >
            <Plus className="w-5 h-5" />
            <span>Создать счёт</span>
          </button>
        </div>
      </div>

      {/* Фильтры */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Поиск */}
          <div className="flex items-center space-x-2">
            <Search className="w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Поиск по номеру или описанию..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="flex-1 border-none outline-none text-gray-900 placeholder-gray-500"
              data-testid="search-invoices-input"
            />
          </div>

          {/* Фильтр по статусу */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
            data-testid="status-filter-select"
          >
            {statusOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          {/* Фильтр по клиенту */}
          <select
            value={clientFilter}
            onChange={(e) => setClientFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
            data-testid="client-filter-select"
          >
            <option value="">Все клиенты</option>
            {clientsData?.clients?.map((client) => (
              <option key={client.id} value={client.id}>
                {client.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Таблица счетов */}
      <div className="bg-white rounded-lg border border-gray-200">
        <DataTable
          columns={columns}
          data={invoicesData?.invoices || []}
          onRowClick={(invoice) => {
            console.log('Invoice clicked:', invoice);
          }}
        />
      </div>

      {/* Диалог создания/редактирования */}
      <InvoiceDialog
        invoice={selectedInvoice}
        isOpen={isDialogOpen}
        onClose={() => {
          setIsDialogOpen(false);
          setSelectedInvoice(null);
        }}
      />

      {/* Итоги массовой загрузки */}
      <UploadResultsDialog
        isOpen={!!bulkResults}
        onClose={() => setBulkResults(null)}
        title="Результаты загрузки счетов"
        summary={bulkResults?.summary}
        results={bulkResults?.results}
      />
    </div>
  );
}